import { ResourceConfigurationInterface } from '@cbsinteractive/avia-js';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AbstractDesktopResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/abstract/AbstractDesktopResourceConfigBuilder';
import { AssetUrlPlaybackUtility } from 'tag/ts/tag/player/adapters/avia/utilities/AssetUrlPlaybackUtility';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad, Stan Gletski
 * @description Builder class to create an instance of ResourceConfigurationInterface for playback via asset url on Desktop
 */
export class DesktopAssetUrlResourceConfigBuilder extends AbstractDesktopResourceConfigBuilder {

	/**
	 * @description Trailing safety window (seconds) applied to `globalResumeTime` on Pluto VOD.
	 * If the resume position lands within this many seconds of the asset's known duration,
	 * resume is skipped and playback starts from 0 instead (PLBCK-8609).
	 */
	static readonly #RESUME_TIME_END_SAFETY_MARGIN_SECONDS = 90;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'DesktopAssetUrlResourceConfigBuilder',
				...argument
			} );

		// init compositional members
		this.assetPlaybackUtil = new AssetUrlPlaybackUtility( argument );
	}

	/**
	 * @description Returns filled resource configuration for playback via asset url on Desktop
	 * @returns {Promise<IPromiseResolveGeneric & {resource: IResourceConfig}>}
	 */
	getResult(): Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }> {
		return new Promise(
			( resolve, reject ) => {

				// get config prefilled with values for Desktop playback
				super.getResult().then(
					( data ) => {
						// Internal avia adapter processing uses native avia-js types
						let resource: ResourceConfigurationInterface = data?.resource;

						// set values for asset url playback
						const usingStitcher = this.hasStitcherParams();
						const isLive = this.tag.model.contentUtilities.isLive();

						resource = this.setMediaUrl( resource );
						resource = this.setDrm( resource );
						if ( !usingStitcher ) {
							resource = this.configureNflLts( resource );
						}
						if ( usingStitcher ) {
							// set ssai={} to enable adbreaks
							resource = this.initSsai( resource );
							// required by avia ad adapter plugin => set live flag for correct adapter selection (Live vs VOD)
							resource = this.setIsLive( resource, isLive );
						}
						resource = this.deepMergeResourceFromSmartTagParams( resource );
						if ( usingStitcher && !isLive ) {
							// After host params merge — honor WITHOUT_RESUME_TIME and globalResumeTime (CP / UEC redirects).
							// PLBCK-8609: skip globalResumeTime entirely when it lands within the trailing safety
							// window of the asset's known duration — an upstream resume-time miscalculation can
							// otherwise seek playback straight into End Card / ECCT range on start.
							if ( this.#isResumeTimeTooCloseToEnd() ) {
								const globalResumeTime = this.params?.CONTENT?.globalResumeTime;
								const duration = this.params?.CONTENT?.apiMetadata?.[ 0 ]?.duration;
								this?.logger?.log( 'setStartTime', 'globalResumeTime within end-of-content safety margin, skipping resume time:', { globalResumeTime, duration } );
							} else {
								resource = this.setStartTime( resource );
							}
						}
						resource = this.sanitizeResource( resource ); // sanitize resource
						resource = this.testOverrides( resource );

						resolve( { msg: 'Resource config', resource: resource } );
					}
				).catch(
					( error ) => {
						// Desktop AssetUrl resource config failed, provide minimal fallback for video playback
						this?.logger?.warn( 'Desktop AssetUrl resource config failed, using minimal configuration:', error );

						// Create minimal resource as fallback to ensure AssetUrl videos can still play
						const basicResource = window.avia.createResourceConfig();
						resolve( { msg: 'Resource config (minimal)', resource: basicResource } );
					}
				);
			}
		);
	}

	/**
	 * @description Sets media url in resource config
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setMediaUrl( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		return super.setMediaUrl( resource, this.assetPlaybackUtil?.getMediaUrl() );
	}

	/**
	 * @description Whether `globalResumeTime` lands within the trailing safety window of the
	 * asset's known duration. Duration is sourced from `apiMetadata[0].duration` — host-supplied
	 * via the Pluto STITCHER bucket and available before the player resolves its own duration.
	 * PLBCK-8609.
	 * @return {boolean}
	 */
	#isResumeTimeTooCloseToEnd(): boolean {
		const globalResumeTime = this.params?.CONTENT?.globalResumeTime;
		const duration = this.params?.CONTENT?.apiMetadata?.[ 0 ]?.duration;

		if ( !this.utils.Type.isNumber( globalResumeTime ) || !this.utils.Type.isNumber( duration ) ) {
			return false;
		}

		// Content no longer than the safety margin itself has no meaningful "near the end" window —
		// treating it as such would discard any legitimate resume position, not just near-end ones.
		if ( duration <= DesktopAssetUrlResourceConfigBuilder.#RESUME_TIME_END_SAFETY_MARGIN_SECONDS ) {
			return false;
		}

		return globalResumeTime >= ( duration - DesktopAssetUrlResourceConfigBuilder.#RESUME_TIME_END_SAFETY_MARGIN_SECONDS );
	}
}